import { useEffect, useState } from "react";

export const Theme = {
	Light: "light",
	Dark: "dark",
	System: "system",
} as const;
export type Theme = (typeof Theme)[keyof typeof Theme];

const THEME_STORAGE_KEY = "modelingstudio:theme";
const THEME_CHANGE_EVENT = "modelingstudio:theme-change";
const DARK_CLASS = "dark";
const PREFERS_DARK_QUERY = "(prefers-color-scheme: dark)";

const isTheme = (value: unknown): value is Theme =>
	value === Theme.Light || value === Theme.Dark || value === Theme.System;

const readStoredTheme = (): Theme => {
	if (typeof window === "undefined") return Theme.System;
	const raw = window.localStorage.getItem(THEME_STORAGE_KEY);
	return isTheme(raw) ? raw : Theme.System;
};

const resolveDark = (theme: Theme): boolean => {
	if (theme === Theme.Dark) return true;
	if (theme === Theme.Light) return false;
	return window.matchMedia(PREFERS_DARK_QUERY).matches;
};

const applyTheme = (theme: Theme): void => {
	document.documentElement.classList.toggle(DARK_CLASS, resolveDark(theme));
};

/**
 * Tri-state theme hook (light / dark / system) with localStorage persistence
 * and cross-instance sync via a custom event so multiple consumers stay in
 * sync without prop drilling. Listens to prefers-color-scheme only while in
 * `system` mode so OS changes propagate live without a refresh.
 */
export const useTheme = (): {
	theme: Theme;
	setTheme: (next: Theme) => void;
	cycleTheme: () => void;
} => {
	const [theme, setThemeState] = useState<Theme>(readStoredTheme);

	useEffect(() => {
		window.localStorage.setItem(THEME_STORAGE_KEY, theme);
		applyTheme(theme);
		window.dispatchEvent(
			new CustomEvent<Theme>(THEME_CHANGE_EVENT, { detail: theme }),
		);
	}, [theme]);

	useEffect(() => {
		const handler = (e: Event) => {
			const detail = (e as CustomEvent<Theme>).detail;
			if (!isTheme(detail)) return;
			setThemeState((prev) => (prev === detail ? prev : detail));
		};
		window.addEventListener(THEME_CHANGE_EVENT, handler);
		return () => window.removeEventListener(THEME_CHANGE_EVENT, handler);
	}, []);

	useEffect(() => {
		if (theme !== Theme.System) return;
		const mq = window.matchMedia(PREFERS_DARK_QUERY);
		const onChange = () => applyTheme(Theme.System);
		mq.addEventListener("change", onChange);
		return () => mq.removeEventListener("change", onChange);
	}, [theme]);

	const setTheme = (next: Theme) => {
		setThemeState(next);
	};

	const cycleTheme = () => {
		setThemeState((prev) => {
			if (prev === Theme.Light) return Theme.Dark;
			if (prev === Theme.Dark) return Theme.System;
			return Theme.Light;
		});
	};

	return { theme, setTheme, cycleTheme };
};

/**
 * Apply the persisted theme synchronously, before React mounts. Prevents the
 * white-flash on first paint when the user has dark stored. Call once from
 * `main.tsx` (or any module imported before `<App />` renders).
 */
export const bootstrapTheme = (): void => {
	if (typeof window === "undefined") return;
	applyTheme(readStoredTheme());
};
